import { CSS_LIST_STYLE_TYPE, DocxNumberFormat } from './docx-conversion-pipeline';
import { ParagraphSpacing } from './docx-xml-model';
import { ResolvedRunFont, ResolvedRunFormatting } from './docx-conversion-pipeline';

export const DEFAULT_DOCX_FONT_FAMILY = 'Cambria';

const LAST_RENDERED_PAGE_BREAK_STYLE_ID = 'DocxLastRenderedPageBreak';
export const LAST_RENDERED_PAGE_BREAK_STYLE_MAP_RULE = `r.${LAST_RENDERED_PAGE_BREAK_STYLE_ID} => hr.page-break`;

function createSectionPageBreakRun() {
  return {
    type: 'run',
    children: [{ type: 'break', breakType: 'page' }],
    styleId: null,
    styleName: null,
    isBold: false,
    isUnderline: false,
    isItalic: false,
    isStrikethrough: false,
    isAllCaps: false,
    isSmallCaps: false,
    verticalAlignment: 'baseline',
    font: null,
    fontSize: null,
    highlight: null,
  };
}

// Runs using one of these named character styles already have an intentional,
// structural style-map mapping (Definition -> our term-definition span, Strong,
// Hyperlink, footnote/endnote anchors) - never overwrite their styleId.
const PROTECTED_RUN_STYLE_NAMES = new Set([
  'definition',
  'strong',
  'hyperlink',
  'footnote reference',
  'endnote reference',
  'footnote anchor',
  'endnote anchor',
]);

const ORDERED_LIST_PATH_TEMPLATES: Array<(cls: string) => string> = [
  (cls) => `ol.${cls} > li:fresh`,
  (cls) => `ul|ol > li > ol.${cls} > li:fresh`,
  (cls) => `ul|ol > li > ul|ol > li > ol.${cls} > li:fresh`,
  (cls) => `ul|ol > li > ul|ol > li > ul|ol > li > ol.${cls} > li:fresh`,
  (cls) => `ul|ol > li > ul|ol > li > ul|ol > li > ul|ol > li > ol.${cls} > li:fresh`,
];

// Mammoth's list matcher only keys off nesting level, not numId, so this can only
// apply one format per level document-wide (see docx-numbering-format.ts). Levels
// using plain decimal need no override (that's the HTML/CSS default already).
export function buildListFormatStyleMapOverrides(dominantFormatsByLevel: Record<number, DocxNumberFormat>): string[] {
  const rules: string[] = [];
  for (let level = 0; level < 5; level++) {
    const format = dominantFormatsByLevel[level];
    if (!format || format === 'decimal' || format === 'bullet') {
      continue;
    }
    const cssClass = `docx-list-${CSS_LIST_STYLE_TYPE[format]}`;
    rules.push(`p:ordered-list(${level + 1}) => ${ORDERED_LIST_PATH_TEMPLATES[level](cssClass)}`);
  }
  return rules;
}

export function buildListFormatCss(dominantFormatsByLevel: Record<number, DocxNumberFormat>): string {
  const classes = new Set<string>();
  Object.values(dominantFormatsByLevel).forEach((format) => {
    if (format && format !== 'decimal' && format !== 'bullet') {
      classes.add(`docx-list-${CSS_LIST_STYLE_TYPE[format]}`);
    }
  });
  return Array.from(classes)
    .map((cls) => `.${cls} { list-style-type: ${cls.replace('docx-list-', '')}; }`)
    .join('\n');
}

interface MammothParagraph {
  type: string;
  styleId?: string | null;
  styleName?: string | null;
  alignment?: string | null;
  numbering?: unknown;
  indent?: {
    start?: string | null;
    end?: string | null;
    firstLine?: string | null;
    hanging?: string | null;
  };
  children?: unknown[];
}

interface MammothRun {
  type: string;
  styleId?: string | null;
  styleName?: string | null;
  font?: string | null;
  fontSize?: number | null;
  isBold?: boolean;
  isItalic?: boolean;
  isUnderline?: boolean;
}

interface MammothTransforms {
  paragraph: (fn: (paragraph: any) => any) => (doc: any) => any;
  run: (fn: (run: any) => any) => (doc: any) => any;
}

// Mirrors mammoth's own default style-map rules (mammoth/lib/options-reader.js):
// "p.Heading1 => h1:fresh" ... "p.Heading6 => h6:fresh", "p.Heading => h1:fresh" (and the
// style-name-keyed equivalents). Returns the resulting tag so callers can point their OWN
// generated style-map rule at the same tag mammoth would have used.
function headingTagName(paragraph: MammothParagraph): string | null {
  const styleId = paragraph.styleId || '';
  const styleName = (paragraph.styleName || '').toLowerCase();

  const idLevelMatch = /^heading([1-6])$/i.exec(styleId);
  if (idLevelMatch) {
    return `h${idLevelMatch[1]}`;
  }
  if (/^heading$/i.test(styleId)) {
    return 'h1';
  }

  const nameLevelMatch = /^heading ([1-6])$/.exec(styleName);
  if (nameLevelMatch) {
    return `h${nameLevelMatch[1]}`;
  }
  if (styleName === 'heading') {
    return 'h1';
  }

  return null;
}

function isHeadingParagraph(paragraph: MammothParagraph): boolean {
  return headingTagName(paragraph) !== null;
}

function twipsToPt(twips: string | number | null | undefined): number | null {
  if (twips === null || twips === undefined) {
    return null;
  }
  const value = typeof twips === 'string' ? parseInt(twips, 10) : twips;
  return Number.isFinite(value) ? Math.round((value / 20) * 100) / 100 : null;
}

function escapeStyleMapString(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

function buildParagraphDeclarations(paragraph: MammothParagraph, spacing: ParagraphSpacing | null): string[] {
  const declarations: string[] = [];

  const alignment = paragraph.alignment;
  if (alignment === 'left' || alignment === 'right' || alignment === 'center') {
    declarations.push(`text-align: ${alignment}`);
  } else if (alignment === 'both') {
    declarations.push('text-align: justify');
  }

  const indent = paragraph.indent;
  if (indent) {
    const startPt = twipsToPt(indent.start);
    const endPt = twipsToPt(indent.end);
    const firstLinePt = twipsToPt(indent.firstLine);
    const hangingPt = twipsToPt(indent.hanging);

    if (startPt) {
      declarations.push(`margin-left: ${startPt}pt`);
    }
    if (endPt) {
      declarations.push(`margin-right: ${endPt}pt`);
    }
    if (firstLinePt) {
      declarations.push(`text-indent: ${firstLinePt}pt`);
    } else if (hangingPt) {
      declarations.push(`text-indent: -${hangingPt}pt`, `padding-left: ${hangingPt}pt`);
    }
  }

  if (spacing) {
    const beforePt = twipsToPt(spacing.beforeTwips);
    const afterPt = twipsToPt(spacing.afterTwips);
    if (beforePt) {
      declarations.push(`margin-top: ${beforePt}pt`);
    }
    if (afterPt) {
      declarations.push(`margin-bottom: ${afterPt}pt`);
    }
  }

  return declarations;
}

// Numbered paragraphs are skipped: mammoth's default list matcher (checked among
// the DEFAULT style-map entries) is what turns them into real <ol>/<li>, and it
// only wins if nothing earlier in the style-map list already matched by styleId -
// which our injected rule would, breaking list detection exactly like the old
// "List Paragraph" rule did. Headings get their own tag-aware handling below (see
// headingTagName) so their <h1>-<h6> mapping survives alongside any direct formatting.
function createParagraphTransform(
  dynamicStyleMap: string[],
  paragraphSpacing: ParagraphSpacing[],
  paragraphHasSectionPageBreak: boolean[],
  paragraphNumberLabel: (string | null)[],
  paragraphNumberIndent: Array<{ leftTwips: number; hangingTwips: number } | null>
) {
  const generatedIdByDeclarations = new Map<string, string>();
  let counter = 0;
  let index = -1;

  return function applyParagraphFidelity(paragraph: MammothParagraph): MammothParagraph {
    index += 1;

    if (paragraphHasSectionPageBreak[index]) {
      paragraph.children = [...(paragraph.children || []), createSectionPageBreakRun()];
    }

    // Heading paragraphs always take priority — no label injection. Their alignment/spacing
    // still needs applying though: mammoth's own default style map turns e.g. a "Heading2"
    // paragraph into <h2> purely by matching that ORIGINAL styleId, so simply reassigning
    // styleId (as done below for plain paragraphs) would silently fall back to mammoth's
    // default <p> rendering and lose the heading tag entirely. Instead point our own
    // generated style-map rule directly at the SAME tag mammoth would have used, so a
    // heading keeps its <h1>-<h6> mapping AND any direct-formatting Word applied on top of
    // the style (e.g. a one-off centered w:jc override on an otherwise left-aligned Heading2).
    const headingTag = headingTagName(paragraph);
    if (headingTag) {
      const spacing = paragraphSpacing[index] || null;
      const declarations = buildParagraphDeclarations(paragraph, spacing);
      if (declarations.length === 0) {
        return paragraph;
      }

      const key = `${headingTag}:${declarations.join(';')}`;
      let styleId = generatedIdByDeclarations.get(key);
      if (!styleId) {
        styleId = `DocxHeading${counter++}`;
        generatedIdByDeclarations.set(key, styleId);
        dynamicStyleMap.push(`p.${styleId} => ${headingTag}[style='${escapeStyleMapString(declarations.join('; '))}']:fresh`);
      }

      paragraph.styleId = styleId;
      paragraph.styleName = styleId;
      return paragraph;
    }

    if (paragraph.numbering) {
      const label = paragraphNumberLabel[index];
      if (label !== null && label !== undefined) {
        paragraph.numbering = undefined;

        const labelRun = {
          type: 'run',
          children: [{ type: 'text', value: label + '\u00A0' }],
          styleId: null,
          styleName: null,
          isBold: false,
          isUnderline: false,
          isItalic: false,
          isStrikethrough: false,
          isAllCaps: false,
          isSmallCaps: false,
          verticalAlignment: 'baseline',
          font: null,
          fontSize: null,
          highlight: null,
        };
        paragraph.children = [labelRun, ...(paragraph.children || [])];

        // Resolved by paragraph index, not by numId read off mammoth's own numbering
        // object - mammoth's numbering object has no numId field (see call site).
        const indentEntry = paragraphNumberIndent[index];
        if (indentEntry && (indentEntry.leftTwips || indentEntry.hangingTwips)) {
          paragraph.indent = {
            ...(paragraph.indent || {}),
            start: String(indentEntry.leftTwips),
            hanging: String(indentEntry.hangingTwips),
          };
        }
      } else {
        // Non-decimal numbered paragraph: preserve mammoth's ol/li pipeline
        return paragraph;
      }
    }

    const spacing = paragraphSpacing[index] || null;
    const declarations = buildParagraphDeclarations(paragraph, spacing);
    if (declarations.length === 0) {
      return paragraph;
    }

    const key = declarations.join(';');
    let styleId = generatedIdByDeclarations.get(key);
    if (!styleId) {
      styleId = `DocxPara${counter++}`;
      generatedIdByDeclarations.set(key, styleId);
      dynamicStyleMap.push(`p.${styleId} => p[style='${escapeStyleMapString(declarations.join('; '))}']:fresh`);
    }

    paragraph.styleId = styleId;
    paragraph.styleName = styleId;
    return paragraph;
  };
}

function createRunTransform(
  dynamicStyleMap: string[],
  runFormattingByStyleId: Record<string, ResolvedRunFormatting>,
  runIsLastRenderedPageBreakMarker: boolean[],
  runParagraphStyleId: (string | null)[],
  runFontByStyleId: Record<string, ResolvedRunFont>,
  runFontDocDefault: ResolvedRunFont
) {
  const generatedIdByKey = new Map<string, string>();
  let counter = 0;
  let index = -1;

  return function applyRunFidelity(run: MammothRun): MammothRun {
    index += 1;

    if (runIsLastRenderedPageBreakMarker[index]) {
      run.styleId = LAST_RENDERED_PAGE_BREAK_STYLE_ID;
      run.styleName = LAST_RENDERED_PAGE_BREAK_STYLE_ID;
      return run;
    }

    const originalStyleName = (run.styleName || '').toLowerCase();
    const isProtectedStyle = PROTECTED_RUN_STYLE_NAMES.has(originalStyleName);

    if (run.styleId && runFormattingByStyleId[run.styleId]) {
      const styleFormatting = runFormattingByStyleId[run.styleId];
      run.isBold = !!run.isBold || styleFormatting.isBold;
      run.isItalic = !!run.isItalic || styleFormatting.isItalic;
      run.isUnderline = !!run.isUnderline || styleFormatting.isUnderline;
    }

    if (isProtectedStyle) {
      return run;
    }

    // mammoth only ever reads a run's own DIRECT w:rFonts/w:sz (see resolveRunFontByStyleId
    // in docx-conversion-pipeline.ts for the full rationale) - it never climbs to the run's
    // character style, its enclosing paragraph's style, or docDefaults the way Word itself
    // does. A run relying on any of those (very common for e.g. table-cell "header" text
    // styled "Heading1" but locally redefined to a small size) would otherwise fall back to
    // the browser's default tag size instead of Word's actual resolved one. Cascade through
    // that same chain here before deciding there's nothing to apply.
    const charStyleFont = run.styleId ? runFontByStyleId[run.styleId] : null;
    const paragraphStyleFont = runFontByStyleId[runParagraphStyleId[index] || 'Normal'];
    const font = run.font ?? charStyleFont?.fontFamily ?? paragraphStyleFont?.fontFamily ?? runFontDocDefault.fontFamily ?? null;
    const fontSize =
      run.fontSize ?? charStyleFont?.fontSizePt ?? paragraphStyleFont?.fontSizePt ?? runFontDocDefault.fontSizePt ?? null;

    if (!font && !fontSize) {
      return run;
    }

    const effectiveFont = font || DEFAULT_DOCX_FONT_FAMILY;
    const key = `${effectiveFont}|${fontSize ?? ''}`;

    let styleId = generatedIdByKey.get(key);
    if (!styleId) {
      styleId = `DocxFont${counter++}`;
      generatedIdByKey.set(key, styleId);

      // Double quotes here deliberately: the whole declaration string is itself
      // wrapped in single quotes for the style-map's own [style='...'] syntax, so
      // single-quoting the font name too would prematurely close that attribute.
      const declarations = [`font-family: "${effectiveFont.replace(/"/g, '')}"`];
      if (fontSize) {
        declarations.push(`font-size: ${fontSize}pt`);
      }
      dynamicStyleMap.push(`r.${styleId} => span[style='${declarations.join('; ')}']`);
    }

    run.styleId = styleId;
    run.styleName = styleId;
    return run;
  };
}

// `dynamicStyleMap` must be the SAME array instance passed as `options.styleMap` to
// mammoth.convertToHtml - see the note at the top of this file for why.
export function createFidelityTransformDocument(
  mammothTransforms: MammothTransforms,
  dynamicStyleMap: string[],
  paragraphSpacing: ParagraphSpacing[],
  runFormattingByStyleId: Record<string, ResolvedRunFormatting>,
  runIsLastRenderedPageBreakMarker: boolean[],
  paragraphHasSectionPageBreak: boolean[],
  paragraphNumberLabel: (string | null)[],
  paragraphNumberIndent: Array<{ leftTwips: number; hangingTwips: number } | null>,
  runParagraphStyleId: (string | null)[],
  runFontByStyleId: Record<string, ResolvedRunFont>,
  runFontDocDefault: ResolvedRunFont
) {
  const paragraphTransform = mammothTransforms.paragraph(
    createParagraphTransform(dynamicStyleMap, paragraphSpacing, paragraphHasSectionPageBreak, paragraphNumberLabel, paragraphNumberIndent)
  );
  const runTransform = mammothTransforms.run(
    createRunTransform(dynamicStyleMap, runFormattingByStyleId, runIsLastRenderedPageBreakMarker, runParagraphStyleId, runFontByStyleId, runFontDocDefault)
  );

  // Run transform MUST walk the tree first, while it's still exactly the runs read
  // from the XML (runIsLastRenderedPageBreakMarker is indexed against that original
  // set). Paragraph transform runs second so the section-break runs it appends are
  // never seen by the run transform's own traversal - see mammoth's transforms.js
  // `elements()`, which recurses element.children at the moment IT runs, not later.
  return function transformDocument(document: any): any {
    return paragraphTransform(runTransform(document));
  };
}
